import React, { FC, useEffect, useRef } from 'react'
import DataLayer, { Impression } from 'lib/dataLayer'
import { useSelector } from 'react-redux'
import { getDataLayerItemVariant, capitalizeString } from 'lib/helpers'
import SALink from 'components/SALink'
import carouselStyles from './PolaroidInfo.module.scss'
import {
  ARTWORK_STATUS,
  getCleanCategory,
  isCategoryInstallation,
  isCategorySculpture,
  getMediumMaterialText,
} from 'components/SA/Polaroid/helpers'
import useQA from 'hooks/useQA'
import { DimensionsUnit } from 'components/MeasurementUnit'
import { convertFromCentsToDollars } from 'components/FormatPrice/helpers'
import PriceStatusRow from 'components/SA/Polaroid/components/PriceStatusRow'
import useIntersectionObserver from 'hooks/intersectionObserver'
import { getIsMobileTraffic } from 'selectors/shared/mobileTraffic'
import { ArtworkObject } from './types'
import { getGeoLocation } from 'selectors/user/cookie'
import { getGeoZoneDestinationCode } from 'lib/storeLocale/client'
import PolaroidActions from 'components/SA/Polaroid/Actions/PolaroidActions'
import { addToCartGAWhitelist } from './helpers'

export type PolaroidInfoProps = {
  artwork: ArtworkObject
  itemPosition: number
  onTitleClick?: (ev: React.SyntheticEvent<Record<string, any>>) => void
  listId: string
  listName: string
  dataApi: string
  showMediumOnMaterial?: boolean
  showArtistName?: boolean
}

const PolaroidInfo: FC<PolaroidInfoProps> = ({
  artwork,
  itemPosition,
  onTitleClick,
  listId,
  listName,
  dataApi,
  showMediumOnMaterial = true,
  showArtistName = true,
}) => {
  const { testId } = useQA()
  const {
    artworkId,
    title,
    productUrl,
    category,
    artistName,
    profileUrl,
    country,
    dimensions,
    sku,
    originalListPrices,
    originalStatus,
    mediums,
    materials,
    hasOpenEditions = false,
    minPrintPrice,
  } = artwork
  const { width, height, depth } = dimensions

  /* Replacing connector */
  const geoLocation = useSelector(getGeoLocation)
  const isMobile = useSelector(getIsMobileTraffic)
  /* ============================================================== */

  const destinationZone = getGeoZoneDestinationCode(geoLocation?.countryCode)
  const listPrice = originalListPrices?.[destinationZone] || 0
  const carouselItemRef = useRef(null)
  const { hasViewed } = useIntersectionObserver(carouselItemRef)
  const shouldOpenNewDesktopPdpTab = !isMobile
  const capitalizedArtworkTitle = capitalizeString(title)
  const capitalizedArtistName = capitalizeString(artistName)
  const capitalizedCountry = country ? capitalizeString(country) : ''
  const cleanCategory = capitalizeString(getCleanCategory(category))
  const isSculpture = isCategorySculpture(category)
  const isInstallation = isCategoryInstallation(category)
  const isSold = originalStatus === ARTWORK_STATUS.SOLD
  const isUnavailable = originalStatus === ARTWORK_STATUS.UNAVAILABLE
  const isReserved = originalStatus === ARTWORK_STATUS.RESERVED
  const categoryOrPrint = cleanCategory
  const polaroidDimensions = {
    width: width,
    height: height,
    depth: depth,
  }
  const originalMediumsMaterial = showMediumOnMaterial
    ? getMediumMaterialText(mediums, materials, category)
    : ''

  useEffect(() => {
    if (hasViewed) {
      // This is the new dataLayer event for GA4
      const impression: Impression = {
        item_category: category.replace(/s$/, '').replace(/\sM/, 'm'),
        item_id: sku,
        item_art_id: artworkId,
        item_name: title,
        item_brand: artistName,
        item_variant: getDataLayerItemVariant(sku),
        index: itemPosition,
        affiliation: 'Online Store',
        price: convertFromCentsToDollars(listPrice) || 0,
        quantity: 1,
        original_availability: originalStatus,
      }
      DataLayer.addImpressionEvent(impression, listId, listName)
    }
  }, [hasViewed])

  const productEvent = addToCartGAWhitelist({
    artistName: capitalizedArtistName,
    category,
    listId: listId,
    price: listPrice,
    sku,
    title: capitalizedArtworkTitle,
    dataPosition: itemPosition,
    artworkId,
  })

  return (
    <div className={carouselStyles.infoWrapper} ref={carouselItemRef}>
      <div className={carouselStyles.infoContainer}>
        <div className={carouselStyles.priceAndAction}>
          <PriceStatusRow
            isSold={isSold}
            isReserved={isReserved}
            isUnavailable={isUnavailable}
            hasOpenEditions={hasOpenEditions}
            minPrintPrice={minPrintPrice}
            listPrice={listPrice}
          />
          <PolaroidActions
            artworkId={artworkId}
            isAddToCartVisible={!isSold}
            sku={sku}
            status={originalStatus}
            price={listPrice}
            productEvent={productEvent}
          />
        </div>
        <div className={carouselStyles.artworkInfo}>
          <h6
            className={carouselStyles.artworkTitle}
            {...testId(`${listId}-carousel-artwork-title`)}
          >
            <SALink
              className={carouselStyles.linkTruncateText}
              alt={`${capitalizedArtworkTitle}, ${categoryOrPrint}`}
              href={productUrl}
              onClick={onTitleClick}
              rel='nofollow'
              title={`View ${capitalizedArtworkTitle} ${categoryOrPrint}`}
              target={shouldOpenNewDesktopPdpTab ? '_blank' : ''}
              data-api={dataApi}
              data-id={sku}
              data-name={title}
              data-position={itemPosition}
              data-list={listId}
              data-category={category}
              data-listid={listId}
              data-listname={listName}
              data-artid={artworkId}
              data-brand={artistName}
              data-price={listPrice}
            >
              {capitalizedArtworkTitle} {categoryOrPrint}
            </SALink>
          </h6>
          {showArtistName ? (
            <p className={carouselStyles.artistName} {...testId(`${listId}-carousel-artist-name`)}>
              <SALink
                className={carouselStyles.linkTruncateText}
                alt={`${capitalizedArtistName} profile link`}
                data-type='profile-url'
                href={profileUrl}
                rel='nofollow'
                title={`View artist ${capitalizedArtistName} profile`}
              >
                {capitalizedArtistName}
                {capitalizedCountry ? `${','} ${capitalizedCountry}` : ''}
              </SALink>
            </p>
          ) : (
            ''
          )}

          {showMediumOnMaterial ? (
            <div className={carouselStyles.mediumAndMaterialContainer}>
              <p className='mediumAndMaterial' {...testId(`${listId}-carousel-medium-material`)}>
                {originalMediumsMaterial}
              </p>
            </div>
          ) : (
            ''
          )}

          <p
            className={carouselStyles.artworkDimensions}
            {...testId(`${listId}-carousel-artwork-dimensions`)}
          >
            <DimensionsUnit
              isDepthVisible={isSculpture || isInstallation}
              dimensions={polaroidDimensions}
            />
          </p>
        </div>
      </div>
    </div>
  )
}

export default PolaroidInfo
